import axios from 'axios';
import {
  Box,
  Button,
  SearchInput,
  TableGroup,
  Text,
  TextInput,
  Touchable,
} from 'plaid-threads';
import React from 'react';
import { isMobile } from 'react-device-detect';
import HeroConsumer from '../assets/hero-home-consumer-right-us-en.gif';
import './index.scss';

interface AdminProps {
  isAdmin: boolean;
}

interface AuthorizedUser {
  email: string;
  items_created: number;
  items_remaining: number;
  expiration: string;
  authorized_by: string;
}

const Admin: React.FC<AdminProps> = (props) => {
  const [email, setEmail] = React.useState<string>('');
  const [authorizationError, setAuthorizationError] =
    React.useState<string>('');
  const [authorizedUsers, setAuthorizedUsers] = React.useState<
    Array<AuthorizedUser>
  >([]);

  const getAuthorizedUsers = React.useCallback(async () => {
    await axios
      .get(`${process.env.REACT_APP_SERVER_URL}/get_authorized_users`, {
        withCredentials: true,
      })
      .catch(function (error) {
        setAuthorizedUsers([]);
      })
      .then(function (response) {
        if (response?.status === 200) {
          setAuthorizedUsers(JSON.parse(response.data.authorized_users));
        }
      });
  }, []);

  const authorizeUser = React.useCallback(
    async (
      evt:
        | React.MouseEvent<Element, MouseEvent>
        | React.KeyboardEvent<HTMLInputElement>,
      email: string,
    ) => {
      evt.preventDefault();
      evt.stopPropagation();
      await axios
        .post(
          `${process.env.REACT_APP_SERVER_URL}/authorize_user`,
          { email: email },
          { withCredentials: true },
        )
        .catch(function (error) {
          setAuthorizationError(error.response.data.error);
        })
        .then(function (response) {
          if (response?.status === 200) {
            setEmail('');
            getAuthorizedUsers();
          }
        });
    },
    [getAuthorizedUsers],
  );

  const revokeAuthorizedUser = React.useCallback(
    async (evt: React.MouseEvent<Element, MouseEvent>, email: string) => {
      evt.preventDefault();
      evt.stopPropagation();
      await axios
        .post(
          `${process.env.REACT_APP_SERVER_URL}/revoke_authorized_user`,
          { email: email },
          { withCredentials: true },
        )
        .catch(function (error) {
          setAuthorizationError(error.response.data.error);
        })
        .then(function (response) {
          if (response?.status === 200) {
            getAuthorizedUsers();
          }
        });
    },
    [getAuthorizedUsers],
  );

  const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      authorizeUser(event, email);
    }
  };

  React.useEffect(() => {
    getAuthorizedUsers();
  }, [getAuthorizedUsers]);

  if (!props.isAdmin) {
    return <div>Not authorized</div>;
  }
  return (
    <div className="Admin__authentication">
      <div style={{ padding: '0 100px 0 0' }}>
        <div className="Admin__titleWrapper">
          <Text appearance="h2" as="h1">
            Admin
          </Text>
          <Text size={20}>
            Authorize an external user to access the Europe Demo.
          </Text>
        </div>
        <div className="Admin__passwordWrapper">
          <TextInput
            id="email"
            label={'Enter an email address'}
            errorMessage={authorizationError}
            onChange={(e) => {
              setEmail(e.target.value);
              setAuthorizationError('');
            }}
            placeholder={'Email address'}
            onKeyPress={handleEnter}
            inputMode="email"
            value={email}
          />
        </div>
        <div className="Admin__experienceWrapper">
          <div>
            <Button
              className="Admin__experienceWrapper__button"
              wide={isMobile}
              centered={isMobile}
              inline
              disabled={!email}
              onClick={(evt) => {
                authorizeUser(evt, email);
              }}
            >
              Authorize
            </Button>
          </div>
          <div></div>
          <div className="Admin__img">
            <img
              src={HeroConsumer}
              width="100%"
              alt="descorative animation depicting a person using a fintech app on their phone while at the park with their dog"
            />
          </div>
        </div>
      </div>
      <div>
        {authorizedUsers && (
          <AuthorizedUsersTableGroup
            authorizedUsers={authorizedUsers}
            renewOnClick={authorizeUser}
            revokeOnClick={revokeAuthorizedUser}
          />
        )}
      </div>
    </div>
  );
};

interface AuthorizedUsersTableProps {
  authorizedUsers: Array<AuthorizedUser>;
  renewOnClick: (
    evt: React.MouseEvent<Element, MouseEvent>,
    email: string,
  ) => void;
  revokeOnClick: (
    evt: React.MouseEvent<Element, MouseEvent>,
    email: string,
  ) => void;
}

const AuthorizedUsersTableGroup: React.FC<AuthorizedUsersTableProps> = ({
  authorizedUsers,
  renewOnClick,
  revokeOnClick,
}) => {
  const [showInactive, setShowInactive] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState<string>('');
  const [authorizedUsersFiltered, setAuthorizedUsersFiltered] =
    React.useState<Array<AuthorizedUser>>(authorizedUsers);

  React.useEffect(() => {
    let filteredUsers = authorizedUsers;
    if (!showInactive) {
      filteredUsers = authorizedUsers.filter((user) => !getIsInactive(user));
    }
    if (search) {
      filteredUsers = filteredUsers.filter((user) =>
        user.email.toLowerCase().includes(search.toLowerCase()),
      );
    }
    setAuthorizedUsersFiltered(filteredUsers);
  }, [showInactive, search, authorizedUsers]);
  return (
    <TableGroup
      label="Existing Authorizations"
      // hasLabel={false}
      className="Admin__table"
      hasPagination
      pagination={{
        rowsPerPage: 7,
      }}
      bulkActions={
        <Box display="flex">
          <Button
            centered
            small
            inline
            onClick={() => {
              setShowInactive(!showInactive);
            }}
          >
            {showInactive
              ? 'Hide Expired/Exhausted/Revoked'
              : 'Show Expired/Exhausted/Revoked'}
          </Button>
        </Box>
      }
      search={
        <SearchInput
          label="Search Emails"
          onChange={(e) => setSearch(e.target.value)}
          onSubmit={(e) => {}}
          placeholder="Search Emails"
          value={search}
        />
      }
      initialState={{
        sortBy: [
          {
            desc: true,
            id: 'expiration',
          },
        ],
      }}
      columns={[
        {
          Header: 'Email',
          accessor: 'email',
        },
        {
          Header: 'Items Created',
          accessor: 'items_created',
          align: 'center',
        },
        {
          Header: 'Items Remaining',
          accessor: 'items_remaining',
          align: 'center',
        },
        {
          Header: 'Expiration (UTC)',
          accessor: 'expiration',
          align: 'center',
        },
        {
          Header: 'Authorized By',
          accessor: 'authorized_by',
          align: 'center',
        },
        {
          Header: 'Actions',
          renderDataCell: (tableData: React.ReactElement) => {
            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Touchable
                  disabled={getIsInactive(tableData.props.row.original)}
                  className="Admin__touchable"
                  onClick={(evt) =>
                    revokeOnClick(evt, tableData.props.row.original.email)
                  }
                  centered
                >
                  Revoke
                </Touchable>
                <Touchable
                  className="Admin__touchable"
                  onClick={(evt) =>
                    renewOnClick(evt, tableData.props.row.original.email)
                  }
                  centered
                >
                  Renew
                </Touchable>
              </div>
            );
          },
          align: 'center',
        },
      ]}
      rows={authorizedUsersFiltered}
    />
  );
};

function getIsInactive(user: AuthorizedUser): boolean {
  const expirationDate = new Date(user.expiration);
  const now = new Date();
  const isExpired = expirationDate < now;
  return isExpired || user.items_remaining === 0;
}

export default Admin;
